import { resolveId } from '@medplum/core'
import { referenceDisplay } from '@seen/core/display'
import { orderCategoryLabel } from '@seen/core/models/orders/constants'
import type { Order } from '@seen/core/models/orders/Order'
import { IconInfoCircle } from '@tabler/icons-react'
import type { JSX } from 'react'
import { Avatar } from '#src/components/core/index.ts'
import { Link } from '#src/components/core/Link.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { IconOrderStatus } from '#src/components/icons/IconOrderStatus.tsx'
import { OrdersList } from '#src/components/order/OrdersList.tsx'
import { useRelatedOrders } from '#src/hooks/useRelatedOrders.ts'
import { getOrderTitle } from '#src/utils/getOrderTitle.ts'

interface RelatedOrdersListProps {
  order: Order
  condensed?: boolean
}

export function RelatedOrdersList({
  order,
  condensed = false,
}: RelatedOrdersListProps): JSX.Element | null {
  const { data: relatedOrders, isLoading } = useRelatedOrders({ order })

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <h3 className="text-lg font-medium">Related Orders</h3>
        <div className="text-sm text-muted-foreground">Loading...</div>
      </div>
    )
  }

  if (!relatedOrders || relatedOrders.length === 0) {
    return null
  }

  if (condensed) {
    return (
      <div className="group/related-orders-info flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">
            Related{' '}
            <span className="font-semibold text-primary">
              {orderCategoryLabel(order.category)}
            </span>{' '}
            Orders
          </p>
          <Tooltip>
            <TooltipTrigger>
              <IconInfoCircle className="size-4 text-muted-foreground opacity-0 group-hover/related-orders-info:opacity-100" />
            </TooltipTrigger>
            <TooltipContent>
              <p>
                Orders related to the current order&apos;s participant based on{' '}
                <span className="font-semibold">category</span>,{' '}
                <span className="font-semibold">requester</span>,{' '}
                <span className="font-semibold">performer</span>,{' '}
                <span className="font-semibold">specialty</span>, and{' '}
                <span className="font-semibold">reason codes</span>.
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="flex flex-col gap-0">
          {relatedOrders.map((relatedOrder) => {
            const title = getOrderTitle(relatedOrder)
            return (
              <Link
                key={relatedOrder.id}
                to={`/Patient/${resolveId(order.participant)}/orders/${relatedOrder.id}`}
                className="flex min-w-0 flex-col items-start gap-1 rounded-md p-2 transition-colors hover:bg-muted/50"
              >
                <div className="flex w-full min-w-0 items-center gap-1.5">
                  <IconOrderStatus
                    status={relatedOrder.status}
                    isSubmitting={relatedOrder.isSubmitting}
                    className="size-3.5 shrink-0"
                  />
                  {relatedOrder.seenIdentifier && (
                    <span className="truncate font-mono text-xs text-muted-foreground">
                      {relatedOrder.seenIdentifier}
                    </span>
                  )}
                  <div className="ml-auto flex shrink-0 justify-end">
                    {relatedOrder.requester && (
                      <Tooltip>
                        <TooltipTrigger>
                          <Avatar
                            profile={relatedOrder.requester}
                            size="icon"
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          {referenceDisplay(relatedOrder.requester)}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </div>
                <p className="w-full truncate text-sm">{title}</p>
              </Link>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="group/related-orders-info flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <p className="text-sm">
          Related{' '}
          <span className="font-semibold text-primary">
            {orderCategoryLabel(order.category)}
          </span>{' '}
          Orders
        </p>
        <Tooltip>
          <TooltipTrigger>
            <IconInfoCircle className="size-4 text-muted-foreground opacity-0 group-hover/related-orders-info:opacity-100" />
          </TooltipTrigger>
          <TooltipContent>
            <p>
              Orders related to the current order&apos;s participant based on{' '}
              <span className="font-semibold">category</span>,{' '}
              <span className="font-semibold">requester</span>,{' '}
              <span className="font-semibold">performer</span>,{' '}
              <span className="font-semibold">specialty</span>, and{' '}
              <span className="font-semibold">reason codes</span>.
            </p>
          </TooltipContent>
        </Tooltip>
      </div>
      <OrdersList
        patientId={resolveId(order.participant)}
        data={relatedOrders}
        isLoading={isLoading}
        error={null}
        hideCheckbox
      />
    </div>
  )
}
